/**
 * PDF Export Utility for Print-Ready Photobooks
 * 
 * This utility exports photobook designs to print-ready PDFs following
 * printing company requirements:
 * 
 * PDF Requirements:
 * - Inside pages saved as single pages in a Multi Page PDF file
 * - No Spreads / No Crop Marks
 * - 3mm bleed is included in the PDF size
 * 
 * Size Reference (with 3mm bleed):
 * - 20x20: 203 x 203 mm
 * - 30x30: 306 x 306 mm  
 * - A4 Landscape: 303 x 216 mm
 * - A4 Portrait: 216 x 303 mm
 * - A4 Portrait Softbook: 213 x 303 mm
 * - A3 Landscape: 410 x 303 mm
 */

import { PDFDocument } from 'pdf-lib';
import { zip } from 'fflate';
import { IScene } from '@layerhub-io/types';
import formatSizes from '~/constants/format-sizes';
import {
  getPdfPageSize,
  getJpegSpreadSize,
  getCoverSize,
  mmToPixels,
  PDF_PAGE_SIZES,
  HARDCOVER_SIZES,
  SOFTCOVER_SIZES,
  EXPORT_QUALITY
} from '~/config/printingConfig';

// Conversion helpers
const mmToPoints = (mm: number): number => mm * 2.834645669;
const pixelsToMm = (pixels: number, dpi: number = 150): number => (pixels / dpi) * 25.4;

export interface ExportOptions {
  format?: 'pdf' | 'jpeg';
  quality?: number;
  dpi?: number;
  includeCovers?: boolean;
  coverType?: 'hardcover' | 'softcover';
  productSize?: string;
  sku?: string;
  onProgress?: (current: number, total: number) => void;
}

export interface ExportProgress {
  stage: string;
  current: number;
  total: number;
}

type InteriorOutputFormat = 'pdf' | 'jpg';

const resolveInteriorOutputFormat = (productSize: string, sku?: string): InteriorOutputFormat => {
  const normalizedSku = (sku || '').toUpperCase();

  // Classic photobooks (non-layflat) always use PDF — no Photographic/Digital distinction.
  if (!productSize.includes('layflat')) return 'pdf';

  // SKU suffix 'PP' = Photographic Paper → prints on the JPG-only machine.
  if (normalizedSku.endsWith('PP')) return 'jpg';

  // SKU suffix 'DP' = Digital Paper (170gsm) → prints as PDF.
  if (normalizedSku.endsWith('DP')) return 'pdf';

  // Landscape Layflatss (A4, A3) and 300x300 only exist as Photographic Paper —
  // their SKUs carry no paper suffix (e.g. LAYA4LSH, LAYA3LPH, LAY300X300LH).
  if (
    productSize === 'A4_Landscape_layflat' ||
    productSize === 'A3_Landscape_layflat' ||
    productSize === '30x30_layflat'
  ) return 'jpg';

  // Fallback (unknown SKU pattern, A5, etc.) → PDF.
  return 'pdf';
};

/**
 * Detect the product size format from frame dimensions
 * Uses the format-sizes.ts data to match frame dimensions
 */
export const detectProductSize = (frameWidth: number, frameHeight: number): string => {
  // Try to find a matching format by pixel dimensions
  for (const format of formatSizes) {
    if (format.pixels && format.printSpecs) {
      const widthMatch = Math.abs(format.pixels.width - frameWidth) < 50;
      const heightMatch = Math.abs(format.pixels.height - frameHeight) < 50;

      if (widthMatch && heightMatch) {
        // Mirror the key logic from printingConfig.ts getFormatKey so that
        // SKU_SIZE_MAP, PDF_PAGE_SIZES, and cover size maps all align.
        const isLayflat = format.name.toLowerCase().includes('layflat') || format.name.toLowerCase().includes('lay-flat');
        const suffix = isLayflat ? '_layflat' : '';

        if (format.name.includes('200x200') || format.name.includes('20x20')) return `20x20${suffix}`;
        if (format.name.includes('300x300') || format.name.includes('30x30')) return `30x30${suffix}`;
        if (format.name.includes('Softcover')) return 'A4_Portrait_Softcover';
        if (format.name.includes('A4') && format.name.includes('Landscape')) return `A4_Landscape${suffix}`;
        if (format.name.includes('A4') && format.name.includes('Portrait')) return `A4_Portrait${suffix}`;
        if (format.name.includes('A5')) return `A5_Portrait${suffix}`;
        if (format.name.includes('A3')) return `A3_Landscape${suffix}`;
      }
    }
  }

  // Fallback to aspect ratio detection (assumes Standard/non-layflat)
  const widthMm = pixelsToMm(frameWidth, 150);
  const heightMm = pixelsToMm(frameHeight, 150);
  const ratio = widthMm / heightMm;

  if (Math.abs(ratio - 1) < 0.1) {
    if (widthMm > 250) return '30x30';
    return '20x20';
  } else if (ratio > 1) {
    if (widthMm > 350) return 'A3_Landscape';
    return 'A4_Landscape';
  } else {
    return 'A4_Portrait';
  }
};

/**
 * Export scenes to a print-ready PDF
 * 
 * @param scenes - Array of scenes to export
 * @param renderer - The editor renderer for generating images
 * @param options - Export options
 * @returns PDF bytes as Uint8Array
 */
export const exportToPrintReadyPdf = async (
  scenes: IScene[],
  renderer: any,
  options: ExportOptions = {}
): Promise<Uint8Array> => {
  const {
    quality = 1.0,
    productSize,
    onProgress
  } = options;

  // Create a new PDF document
  const pdfDoc = await PDFDocument.create();

  // Determine product size from first scene if not provided
  const firstScene = scenes[0];
  const detectedSize = productSize || detectProductSize(
    firstScene.frame?.width || 3570,
    firstScene.frame?.height || 2546
  );

  // Get the PDF page size (in mm, then convert to points)
  const pageSizeMm = getPdfPageSize(detectedSize);
  const pageWidthPts = mmToPoints(pageSizeMm.width);
  const pageHeightPts = mmToPoints(pageSizeMm.height);

  console.log(`Exporting PDF: ${detectedSize} - ${pageSizeMm.width}x${pageSizeMm.height}mm (${pageWidthPts}x${pageHeightPts}pts)`);

  // Process each scene
  for (let index = 0; index < scenes.length; index++) {
    const scene = scenes[index];

    if (onProgress) {
      onProgress(index + 1, scenes.length);
    }

    // Add a new page with the printing size (includes 3mm bleed)
    const page = pdfDoc.addPage([pageWidthPts, pageHeightPts]);

    try {
      // Calculate the exact pixelRatio needed to hit 300 DPI output.
      // Scenes are already sized at 300 DPI pixels (see format-sizes.ts),
      // so this typically yields pixelRatio = 1 — avoiding unnecessary upscaling.
      const sceneWidth = scene.frame?.width || pageSizeMm.width;
      const sceneHeight = scene.frame?.height || pageSizeMm.height;
      const targetWidthPx = Math.round(mmToPixels(pageSizeMm.width, 300));
      const targetHeightPx = Math.round(mmToPixels(pageSizeMm.height, 300));
      const pixelRatio = Math.max(1, Math.min(4,
        Math.max(targetWidthPx / sceneWidth, targetHeightPx / sceneHeight)
      ));

      // NON-BLOCKING: renderToBlob uses canvas.toBlob() which runs the
      // heavy JPEG encoding on the browser's internal thread.
      const blob: Blob = await renderer.renderToBlob(scene, {
        pixelRatio,
        format: 'image/jpeg',
        quality: EXPORT_QUALITY.PDF.imageQuality,
      });

      // Yield so the progress bar can repaint before the next CPU-bound step
      await new Promise(resolve => requestAnimationFrame(resolve));

      // Blob → Uint8Array — no base64 encode/decode overhead
      let imageBytes: Uint8Array | null = new Uint8Array(await blob.arrayBuffer());

      console.log(`Scene ${index + 1}: JPEG blob ${imageBytes.byteLength} bytes`);

      const embeddedImage = await pdfDoc.embedJpg(imageBytes);
      imageBytes = null; // Allow GC to reclaim

      page.drawImage(embeddedImage, {
        x: 0,
        y: 0,
        width: pageWidthPts,
        height: pageHeightPts,
      });
    } catch (error) {
      console.error(`Error rendering scene ${index + 1}:`, error);
    }

    // Yield between scenes
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  // Serialize the PDF document to bytes
  const pdfBytes = await pdfDoc.save();

  return pdfBytes;
};

/**
 * Export a single scene as a high-quality JPEG (for covers)
 * 
 * @param scene - Scene to export
 * @param renderer - The editor renderer
 * @param coverType - Type of cover (hardcover/softcover)
 * @param productSize - Product size format
 * @returns Base64 JPEG data URL
 */
export const exportCoverAsJpeg = async (
  scene: IScene,
  renderer: any,
  coverType: 'hardcover' | 'softcover' = 'hardcover',
  productSize: string = '20x20'
): Promise<string> => {
  const coverSize = getCoverSize(productSize, coverType);

  console.log(`Exporting cover JPEG: ${productSize} ${coverType} - ${coverSize.width}x${coverSize.height}mm`);

  // Render the scene at high quality
  const imageDataUrl = await renderer.render(scene, {
    format: 'image/jpeg',
    quality: EXPORT_QUALITY.COVER.imageQuality
  });

  return imageDataUrl;
};

/**
 * Export all scenes as a ZIP file containing:
 *   - inside-pages.pdf  (all interior scenes, one page per scene)
 *   - cover.jpg         (Layflat) or cover-front.jpg + cover-back.jpg (Standard)
 *
 * Yields to the browser between renders so the UI stays responsive.
 * Calls onProgress after every rendered scene so the caller can show a progress bar.
 *
 * @param scenes      - Full scenes array (cover first, back-cover last for Standard)
 * @param renderer    - The editor renderer
 * @param isLayflat   - Whether this is a Layflat product (affects cover split)
 * @param productSize - Resolved product size key (from detectProductSize / SKU)
 * @param onProgress  - Called after each render step
 * @param filename    - Base name for the downloaded zip (no extension)
 */
export const exportAllAsZip = async (
  scenes: IScene[],
  renderer: any,
  productSize: string,
  sku: string | undefined,
  onProgress: (progress: ExportProgress) => void,
  filename: string = 'photobook'
): Promise<void> => {
  // Personalised Hardcover (PH in SKU) includes the full cover spread (front + back + spine)
  // as scene 0 — exported separately as cover.jpg.
  // Standard / Luxury Hardcovers have no designer cover: all scenes are interior pages.
  const isPersonalisedCover = (sku || '').toUpperCase().includes('PH');
  const coverIndices = isPersonalisedCover ? new Set([0]) : new Set<number>();

  const coverScenes = scenes.filter((_, i) => coverIndices.has(i));
  const interiorScenes = scenes.filter((_, i) => !coverIndices.has(i));

  const totalSteps = interiorScenes.length + coverScenes.length;
  let completedSteps = 0;

  const zipEntries: { [name: string]: Uint8Array } = {};
  const interiorOutputFormat = resolveInteriorOutputFormat(productSize, sku);

  // ── Interior pages → PDF ─────────────────────────────────────────────────
  if (interiorScenes.length > 0 && interiorOutputFormat === 'pdf') {
    const pdfBytes = await exportToPrintReadyPdf(interiorScenes, renderer, {
      productSize,
      sku,
      onProgress: (current, total) => {
        onProgress({
          stage: `Rendering page ${current} of ${total}`,
          current: completedSteps + current - 1,
          total: totalSteps,
        });
      },
    });
    zipEntries['inside-pages.pdf'] = pdfBytes;
    completedSteps = interiorScenes.length;
  }

  // ── Interior pages → JPG sequence ────────────────────────────────────────
  if (interiorScenes.length > 0 && interiorOutputFormat === 'jpg') {
    const spreadSizeMm = getJpegSpreadSize(productSize);
    const targetWidthPx = Math.round(mmToPixels(spreadSizeMm.width, 300));
    const targetHeightPx = Math.round(mmToPixels(spreadSizeMm.height, 300));

    for (let i = 0; i < interiorScenes.length; i++) {
      const scene = interiorScenes[i];
      onProgress({
        stage: `Rendering interior JPG ${i + 1} of ${interiorScenes.length}`,
        current: completedSteps + i,
        total: totalSteps,
      });

      await new Promise(resolve => requestAnimationFrame(resolve));

      const frameWidth = scene.frame?.width || targetWidthPx;
      const frameHeight = scene.frame?.height || targetHeightPx;
      const pixelRatio = Math.max(
        1,
        Math.min(4, Math.max(targetWidthPx / frameWidth, targetHeightPx / frameHeight))
      );

      const jpgBlob: Blob = await renderer.renderToBlob(scene, {
        format: 'image/jpeg',
        quality: EXPORT_QUALITY.PDF.imageQuality,
        pixelRatio,
      });
      let bytes: Uint8Array | null = new Uint8Array(await jpgBlob.arrayBuffer());
      const pageNumber = String(i + 1).padStart(3, '0');
      zipEntries[`inside-pages-${pageNumber}.jpg`] = bytes;
      bytes = null; // Allow GC to reclaim
    }

    completedSteps = interiorScenes.length;
  }

  // ── Cover scene(s) → JPEG ────────────────────────────────────────────────
  for (let i = 0; i < coverScenes.length; i++) {
    const label = coverScenes.length > 1
      ? (i === 0 ? 'front cover' : 'back cover')
      : 'cover';

    onProgress({ stage: `Rendering ${label}`, current: completedSteps, total: totalSteps });

    // Yield before the heavy render so progress text can paint
    await new Promise(resolve => requestAnimationFrame(resolve));

    // NON-BLOCKING: renderToBlob encodes JPEG off the main thread
    const coverBlob: Blob = await renderer.renderToBlob(coverScenes[i], {
      format: 'image/jpeg',
      quality: EXPORT_QUALITY.COVER.imageQuality,
    });

    // Yield after encoding so the UI can update
    await new Promise(resolve => requestAnimationFrame(resolve));

    // Blob → Uint8Array — no base64 round-trip needed
    const bytes = new Uint8Array(await coverBlob.arrayBuffer());

    const coverFilename = coverScenes.length > 1
      ? (i === 0 ? 'cover-front.jpg' : 'cover-back.jpg')
      : 'cover.jpg';

    zipEntries[coverFilename] = bytes;
    completedSteps++;
  }

  // ── Create ZIP (async so the main thread isn't blocked) ──────────────────
  onProgress({ stage: 'Creating ZIP…', current: totalSteps, total: totalSteps });

  const zipData = await new Promise<Uint8Array>((resolve, reject) => {
    // level: 0 = STORE — fastest for already-compressed JPEG / PDF data
    zip(zipEntries, { level: 0 }, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });

  // ── Trigger browser download ──────────────────────────────────────────────
  const blob = new Blob([zipData.buffer as ArrayBuffer], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.zip`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Download PDF file to user's device
 *
 * @param pdfBytes - PDF bytes to download
 * @param filename - Name for the downloaded file
 */
export const downloadPdf = (pdfBytes: Uint8Array, filename: string = 'photobook.pdf'): void => {
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  // Clean up
  URL.revokeObjectURL(url);
};

/**
 * Download JPEG file to user's device
 * 
 * @param imageDataUrl - Base64 image data URL
 * @param filename - Name for the downloaded file
 */
export const downloadJpeg = (imageDataUrl: string, filename: string = 'cover.jpg'): void => {
  const link = document.createElement('a');
  link.href = imageDataUrl;
  link.download = filename;
  link.click();
};

// Export shared functions and constants for usage elsewhere
export {
  getPdfPageSize,
  getCoverSize,
  PDF_PAGE_SIZES,
  HARDCOVER_SIZES,
  SOFTCOVER_SIZES
};

export default {
  exportToPrintReadyPdf,
  exportCoverAsJpeg,
  exportAllAsZip,
  downloadPdf,
  downloadJpeg,
  detectProductSize,
  getPdfPageSize,
  getCoverSize,
  PDF_PAGE_SIZES,
  HARDCOVER_SIZES,
  SOFTCOVER_SIZES
};
